import { getSentryConfig } from './config';
import { flushSentry, logSentryConfig } from './helpers';
import { trackSentryException } from '@/src/infrastructure/lib/analytics';

let sentryInitialized = false;

export async function initSentry(): Promise<boolean> {
  const config = getSentryConfig();
  logSentryConfig(config);

  if (sentryInitialized) {
    // eslint-disable-next-line no-console
    console.warn('[sentry] already initialized');
    return true;
  }

  if (!config.dsn) {
    sentryInitialized = false;
    // eslint-disable-next-line no-console
    console.warn('[sentry] disabled: missing VITE_SENTRY_DSN');
    return false;
  }

  try {
    const Sentry = await import('@sentry/react');
    Sentry.init({
      dsn: config.dsn,
      environment: config.environment,
      release: config.release,
      tracesSampleRate: config.tracesSampleRate,
      debug: config.debug,
      initialScope: {
        tags: {
          service: config.serviceName,
          runtime: 'browser',
          serverName: config.serverName,
        },
      },
      beforeSend(event) {
        if (event.exception && event.event_id) {
          trackSentryException(
            event.exception.values?.[0]?.value || 'Unhandled exception',
            event.event_id,
          );
        }
        return event;
      },
    });

    Sentry.setTag('service', config.serviceName);
    Sentry.setTag('runtime', 'browser');
    Sentry.setTag('serverName', config.serverName);

    sentryInitialized = Boolean(Sentry.getClient());
    // eslint-disable-next-line no-console
    console.info('[sentry] initialized', {
      initialized: sentryInitialized,
      environment: config.environment,
      release: config.release,
    });

    if (config.smokeTest) {
      const eventId = Sentry.captureException(
        new Error('InviteCloud frontend Sentry smoke test'),
      );
      // eslint-disable-next-line no-console
      console.info('[sentry] smoke test event captured', { eventId });
      await flushSentry(Sentry, 5000);
    }

    return sentryInitialized;
  } catch (err) {
    sentryInitialized = false;
    // eslint-disable-next-line no-console
    console.warn('[sentry] failed to initialize:', (err as Error).message);
    return false;
  }
}

/**
 * Report a non-crash condition (e.g. a template node we cannot render) to Sentry as a *message* with
 * level "warning". It stays visible in Sentry but is deliberately NOT an exception: `beforeSend` only
 * forwards events that carry `exception` to GA4, so these never inflate GA4's `exception` count, which
 * should mean real errors only.
 */
export async function captureFrontendMessage(
  message: string,
  level: 'warning' | 'info' = 'warning',
): Promise<string | undefined> {
  if (!sentryInitialized) return undefined;

  try {
    const Sentry = await import('@sentry/react');
    const eventId = Sentry.captureMessage(message, level);
    await flushSentry(Sentry, 5000);
    return eventId;
  } catch (captureErr) {
    // eslint-disable-next-line no-console
    console.warn('[sentry] message capture failed:', (captureErr as Error).message);
    return undefined;
  }
}

export async function captureFrontendException(err: unknown): Promise<string | undefined> {
  if (!sentryInitialized) return undefined;

  try {
    const Sentry = await import('@sentry/react');
    const eventId = Sentry.captureException(err);
    // eslint-disable-next-line no-console
    console.info('[sentry] event captured', { eventId });
    await flushSentry(Sentry, 5000);
    return eventId;
  } catch (captureErr) {
    // eslint-disable-next-line no-console
    console.warn('[sentry] capture failed:', (captureErr as Error).message);
    return undefined;
  }
}
